import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Server, Shield, Activity, HelpCircle } from 'lucide-react';
import { PRODUCTS } from '../constants';

const ProductDetailPage = () => {
  const { id } = useParams();
  const product = PRODUCTS.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h2>
        <Link to="/products" className="text-brand-600 hover:underline flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in bg-white">
      {/* Hero Header */}
      <section className="bg-slate-900 text-white pt-24 pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/products" className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{product.name}</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl leading-relaxed">
            {product.shortDescription}
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Product Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
               <div className="mb-8 rounded-xl overflow-hidden shadow-sm h-64 md:h-80 w-full">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {product.longDescription}
              </p>
            </div>

            {/* Features List */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Top Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start p-3 bg-slate-50 rounded-lg">
                    <Check className="w-5 h-5 text-brand-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">How It Works</h2>
              <div className="space-y-8 relative before:absolute before:left-[19px] before:top-2 before:h-full before:w-0.5 before:bg-gray-200">
                <div className="relative pl-12">
                  <div className="absolute left-0 top-0 w-10 h-10 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center border-4 border-white font-bold">1</div>
                  <h3 className="text-lg font-bold text-gray-900">Setup & Integration</h3>
                  <p className="text-gray-600 mt-2">
                    Rapid onboarding with data migration tools. We configure the environment to match your specific workflow requirements.
                  </p>
                </div>
                <div className="relative pl-12">
                  <div className="absolute left-0 top-0 w-10 h-10 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center border-4 border-white font-bold">2</div>
                  <h3 className="text-lg font-bold text-gray-900">Daily Operations</h3>
                  <p className="text-gray-600 mt-2">
                    Your team uses intuitive web and mobile interfaces to manage core tasks, while automation handles background processes.
                  </p>
                </div>
                 <div className="relative pl-12">
                  <div className="absolute left-0 top-0 w-10 h-10 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center border-4 border-white font-bold">3</div>
                  <h3 className="text-lg font-bold text-gray-900">Scale & Analytics</h3>
                  <p className="text-gray-600 mt-2">
                    Monitor performance via real-time dashboards. Scale effortlessly with our cloud-native architecture.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <HelpCircle className="text-brand-500" /> FAQ
              </h2>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Can this integrate with existing systems?</h4>
                  <p className="text-gray-600 text-sm">Yes, our solutions offer REST APIs and standard connectors for common ERP and CRM platforms.</p>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Is the mobile app offline-capable?</h4>
                  <p className="text-gray-600 text-sm">Absolutely. We prioritize offline-first architecture, ensuring data syncs automatically when connectivity returns.</p>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <h4 className="font-semibold text-gray-900 mb-2">How is data security handled?</h4>
                  <p className="text-gray-600 text-sm">We use enterprise-grade encryption (AES-256) for data at rest and TLS 1.3 for data in transit.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              {/* Product Info Card */}
              <div className="bg-gradient-to-br from-brand-50 to-sky-50 rounded-xl shadow-lg border border-brand-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">About This Product</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  {product.shortDescription}
                </p>
                <Link
                  to="/about"
                  className="link-with-arrow inline-flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700"
                >
                  Learn more about us <ArrowRight className="arrow-icon w-4 h-4 ml-1" />
                </Link>
              </div>

              {/* Industry Fit */}
              <div className="bg-brand-50 rounded-xl p-6 border border-brand-100">
                <h3 className="text-sm font-bold text-brand-900 uppercase tracking-wide mb-3">Industry Fit</h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-brand-800">
                    <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                    Enterprise & Corporate
                  </li>
                   <li className="flex items-center text-sm text-brand-800">
                    <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                    Mid-Market Growth
                  </li>
                   <li className="flex items-center text-sm text-brand-800">
                    <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2"></div>
                    High-Volume Operations
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;

